import React, { memo, useEffect, useRef, useCallback, useState } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { pathToRegexp } from 'path-to-regexp'
import cx from 'classnames'

import { makeStyles } from '@material-ui/core/styles'
import { Divider, IconButton, List, ListItem, Tooltip, Typography, Grid } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import { PinDrop } from '@material-ui/icons'

import WhatsIcon from '~/components/WhatsIcon'
import StoreNotFound from '~/components/StoreNotFound'
import { useLiterals } from '~/components/Literals'

import Placeholder from './Placeholder'
import IfoodBanner from './ifoodBanner'

import WhatsappFormDialog from '~/containers/WhatsAppForm'
import { convertPeriod } from '~/containers/StoreInformations/utils/convert-period'

import { formatDistance, getWhatsAppUrl } from '~/utils'
import track, { cliqueRota, cliqueWhats, cliqueLoja } from '~/track'
import { selectStores } from '~/selectors'
import actions from '~/actions'
import api from '~/api'
import useIsMount from '../useIsMount'

const useStyles = makeStyles((theme) => ({
  list: {
    marginTop: -10,
    [theme.breakpoints.up('md')]: {
      marginBottom: 92
    }
  },
  itemList: {
    paddingBlock: 24,
    alignItems: 'start'
  },
  text: {
    paddingRight: 8,
    width: '100%',
    '& .single-line': {
      ...theme.gofind.lineLimit(2),
      '&:first-child': {
        fontSize: 24,
        fontWeight: 'bold',
        ...theme.gofind.lineLimit()
      }
    }
  },
  featured: {
    background: '#fffbc8'
  },
  distance: {
    textAlign: 'right',
    display: 'flex',
    alignItems: 'center',
    marginLeft: 16,
    gap: 16
  },
  item: {
    gap: 8,
    display: 'flex',
    alignItems: 'center',
    marginTop: 16
  },
  badge: {
    width: 8,
    height: 8,
    borderRadius: 9999,
    '&.open': { backgroundColor: '#A6CD30' },
    '&.close': { backgroundColor: '#f44336' }
  }
}))

function randMessage(arr) {
  return arr[Math.floor(Math.random() * arr.length)]
}

export function getStockMessage(store, product, getLiteral) {
  if (store.featured) return store.featured.message

  if (!product || !store.stats) return null

  const stockLevel = store.stats[product.gtin]?.stockLevel

  if (!stockLevel) return ''

  switch (stockLevel) {
    case '1':
      return randMessage([getLiteral('2010'), getLiteral('2011')])
    case '2':
      return randMessage([getLiteral('2012')])
    case '3':
      return randMessage([getLiteral('2013'), getLiteral('2014')])
    default:
      return randMessage([getLiteral('2012')])
  }
}

function Store({
  classes,
  last,
  onMouseEnter,
  product,
  config,
  store,
  position,
  openWhatsForm,
  trackClickWhatsapp
}) {
  const { name, distance, coordinates, cnpj, whatsapp, gmbStatus } = store
  const { getLiteral } = useLiterals()

  const history = useHistory()
  const dispatch = useDispatch()

  const handleClick = useCallback(() => {
    cliqueLoja(config, store, product, position.user)
    dispatch(actions.selectStore(store))
    history.push(`/loja/${btoa(cnpj)}`)
  }, [cnpj, store.name])

  const [period, setPeriod] = useState(null)
  const [isLoadingPeriod, setIsLoadingPeriod] = useState(false)

  const brandName = config?.brandName
  const isEstablishment = Boolean(config?.showContextViewEstablishment)

  const currentDateInMillis = new Date().getTime()
  const storeFeaturedWithLimitDate =
    store?.featured?.limitDate && new Date(store?.featured?.limitDate).getTime()
  const isWithinLimit = currentDateInMillis <= storeFeaturedWithLimitDate

  useEffect(() => {
    async function handleGmbStore() {
      setPeriod(null)

      if (gmbStatus === 'INTEGRATED') {
        try {
          setIsLoadingPeriod(true)

          const response = await api.gmbStore(cnpj)
          const { data } = response

          if (data?.regularHours && JSON.stringify(data?.regularHours) !== '{}') {
            const periodFormatted = convertPeriod(data?.regularHours.periods)
            const { isOpen, endTime } = periodFormatted

            setPeriod({
              isOpen,
              openedUntil: endTime.includes(':00') ? endTime.replace(':00', 'h') : endTime
            })
          }
        } catch (err) {
          setPeriod(null)
          console.log('TCL: ~ handleGmbStore ~ err', err)
        } finally {
          setIsLoadingPeriod(false)
        }

        return
      }

      setPeriod(null)
      return
    }

    handleGmbStore()
  }, [gmbStatus])

  return (
    <>
      <ListItem
        button
        className={cx(classes.itemList, {
          [classes.featured]: Boolean(store?.featured) && isWithinLimit
        })}
        onClick={handleClick}
        onMouseEnter={onMouseEnter}
        onFocus={onMouseEnter}
        data-cy={`store-${name}`}
        data_analytics_category="onde-comprar"
        data_analytics_action="select_partner"
        data_analytics_partner_name={store?.name}
        data_analytics_partner_address={store?.coordinates?.formattedAddress}
        data_analytics_product_id={product?.gtin}
        data_analytics_product_name={product?.name}
        data_analytics_product_size={product?.name}
        data_analytics_product_type={product?.name}
        data_analytics_local={position?.user?.city}
      >
        <div className={classes.text}>
          <Typography variant="h1" color="inherit" className="single-line">
            {name}
          </Typography>

          <Typography variant="body1" color="inherit" className="single-line">
            {coordinates.formattedAddress}
          </Typography>

          {isLoadingPeriod ? (
            <Skeleton variant="rect" width="80%" height={20} style={{ marginTop: 16 }} />
          ) : null}

          {period ? (
            <Grid className={classes.item}>
              <Grid className={`${classes.badge} ${period.isOpen ? 'open' : 'close'}`} />
              <Typography variant="body1">
                {period.isOpen
                  ? `${getLiteral('2090')} (${getLiteral('2558', [period.openedUntil])})`
                  : `${getLiteral('2091')}`}
              </Typography>
            </Grid>
          ) : null}
        </div>

        <div className={classes.distance}>
          {whatsapp && (
            <Tooltip
              title={getLiteral('2188')}
              PopperProps={{ container: document.getElementById('gofind-finder') || document.body }}
            >
              {config.whatsappForm ? (
                <IconButton
                  style={{ padding: 0 }}
                  onClick={(e) => {
                    trackClickWhatsapp()
                    openWhatsForm()
                    e.stopPropagation()
                  }}
                >
                  <WhatsIcon color="inherit" fullColor />
                </IconButton>
              ) : (
                <IconButton
                  style={{ padding: 0 }}
                  onClick={(e) => {
                    trackClickWhatsapp()
                    e.stopPropagation()
                  }}
                  component="a"
                  href={getWhatsAppUrl(
                    whatsapp,
                    name,
                    product?.name ||
                      Object.values(store.stats)
                        .map((i) => i.name)
                        .join(),
                    brandName,
                    isEstablishment,
                    getLiteral
                  )}
                  target="_blank"
                >
                  <WhatsIcon color="inherit" fullColor />
                </IconButton>
              )}
            </Tooltip>
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {!!distance ? (
              <Tooltip
                title={getLiteral('2474')}
                PopperProps={{
                  container: document.getElementById('gofind-finder') || document.body
                }}
              >
                <IconButton
                  onClick={() => {
                    cliqueRota(config, store, product, position?.user)
                  }}
                  component="a"
                  target="_blank"
                  style={{ padding: 8 }}
                >
                  <PinDrop color="primary" style={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            ) : null}
            <Typography>{formatDistance(distance)}</Typography>
          </div>
        </div>
      </ListItem>
      {!last && <Divider />}
    </>
  )
}

const MemoizedStore = memo(Store)
const routersWhiteList = pathToRegexp(
  ['/produto/:fakeName/:gtin', '/produto/:gtin', '/produto/todos'],
  null,
  { sensitive: true }
)

const StoreList = ({ currentProduct, storeHover, setHover, position }) => {
  const config = useSelector((store) => store.config)
  const isMounted = useIsMount()
  const { stores, loading } = useSelector(selectStores, shallowEqual)
  const { showContextViewStore } = config
  const history = useHistory()

  const classes = useStyles({ storesNameViewFormat: config.storesNameViewFormat })
  const dispatch = useDispatch()
  const currentStores = useRef(null)

  const [whatsappForm, setWhatsAppForm] = useState(false)
  const isProductPage = routersWhiteList.test(history?.location?.pathname)

  useEffect(() => {
    if (isMounted && isProductPage && config && stores?.length > 0 && !loading) {
      if (JSON.stringify(currentStores.current) === JSON.stringify(stores)) return

      track('whatsappTotal', {
        name: stores.filter(({ whatsapp }) => {
          return whatsapp && whatsapp.trim()
        }).length,
        category: 'lojas'
      })

      currentStores.current = stores
    }
  }, [stores, config])

  const trackClickWhatsapp = useCallback(() => {
    currentProduct &&
      Object.values(currentProduct).some((v) => Boolean(v)) &&
      cliqueWhats(currentProduct)
  }, [currentProduct])

  const openWhatsappForm = (store) => {
    dispatch(actions.updateApp({ currentStore: store }))
    setWhatsAppForm(true)
  }

  if (loading || !currentProduct) return <Placeholder />

  if (!loading && (!stores || !stores.length)) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: 20
        }}
      >
        <StoreNotFound />
      </div>
    )
  }

  return (
    <>
      <WhatsappFormDialog
        open={whatsappForm}
        setOpen={setWhatsAppForm}
        track={trackClickWhatsapp}
      />

      <List className={classes.list}>
        {currentProduct?.gtin === 'all' && showContextViewStore && stores.length > 10 ? (
          stores.slice(0, 10).map((store, index) => (
            <Grid key={store.cnpj}>
              <MemoizedStore
                product={currentProduct}
                config={config}
                store={store}
                last={index === store.length - 1}
                storeHover={storeHover === index}
                classes={classes}
                position={position}
                onMouseEnter={() => setHover(index)}
                onMouseLeave={() => setHover(null)}
                openWhatsForm={() => openWhatsappForm(store)}
                trackClickWhatsapp={trackClickWhatsapp}
              />
              {config?.iFoodApi && index === 1 && (
                <>
                  <IfoodBanner url={config?.iFoodApi} />
                  <Divider key={`key-ifood-${store.cnpj}-divider1`} />
                </>
              )}
            </Grid>
          ))
        ) : (
          <>
            {stores.map((store, index) => (
              <Grid key={store.cnpj}>
                <MemoizedStore
                  product={currentProduct}
                  config={config}
                  store={store}
                  last={index === store.length - 1}
                  storeHover={storeHover === index}
                  classes={classes}
                  position={position}
                  onMouseEnter={() => setHover(index)}
                  onMouseLeave={() => setHover(null)}
                  openWhatsForm={() => openWhatsappForm(store)}
                  trackClickWhatsapp={trackClickWhatsapp}
                />
                {config?.iFoodApi && (stores.length <= 1 || index === 1) && (
                  <>
                    <IfoodBanner url={config?.iFoodApi} />
                    <Divider key={`key-ifood-${store.cnpj}-divider2`} />
                  </>
                )}
              </Grid>
            ))}
          </>
        )}
      </List>
    </>
  )
}

export default memo(StoreList)
